import {
  createContext,
  useReducer,
  useContext,
  Fragment,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react'
import PropTypes from 'prop-types'
import { rgba } from 'polished'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import { gradient, text } from 'chaoskit/src/assets/styles/utility'
import qs from 'qs'
import {
  Alert,
  Button,
  Container,
  Input,
  Range,
  Section,
  Tooltip,
} from 'chaoskit/src/components'
import { isEmpty } from 'lodash'
import { StylesRangeVariables } from 'chaoskit/src/components/Range'
import { tealGreen } from 'chaoskit/src/assets/styles/utility/gradient'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import {
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import Hero, { HeroContent, HeroTitleSub } from '~partials/Hero'
import { ReactComponent as HeroSVG } from '~media/roi/hero.svg'
import { ReactComponent as GartnerSVG } from '~media/logos/gartner.svg'
import Logo from '~components/Logo'
import Quote from '~components/Quote'
import Data from '~components/Data'
import Icon from '~components/Icon'
import { formatPrice } from '~helpers/utility'
import roiCalculations from '~helpers/roi/calculations'
import track from '~helpers/track'
import TextLink from '~components/TextLink'

const ROIContext = createContext()

const initialState = {
  R: 100000,
  N: 1000,
  CL: 1,
  DO: 5,
  D: 14.4,
  S: 8,
  HR: 30,
  E: 50,
  ES: 130000,
  U: 1000,
  US: 70000,
  P: 20,
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'update':
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const ROIProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const value = { state, dispatch }

  return <ROIContext.Provider value={value}>{children}</ROIContext.Provider>
}

ROIProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

function generateUrl(state) {
  return typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}?${qs.stringify(
        state
      )}`
    : ''
}

/* eslint-enable no-bitwise */

function getLabel(value, type) {
  switch (type) {
    case 'percentage':
      return `${value}%`
    case 'currency':
      return formatPrice(value)
    case 'monthly':
      return `${value} per month`
    default:
      return value
  }
}

const CalculatorQuestionInput = ({ control }) => {
  const theme = useTheme()

  const [inputActive, setInputActive] = useState(false)
  const inputRef = useRef()

  // Calculates the offset of the range thumb size - the track / 2 so we can line up the border as needed of the aside input
  const rangeThumbOffset =
    (StylesRangeVariables(theme).thumb.size -
      StylesRangeVariables(theme).track.height) /
    2

  const {
    dispatch,
    state: { [control.name]: valueVariable },
  } = useContext(ROIContext)

  return (
    <div
      css={{
        position: 'relative',
        paddingBottom: rangeThumbOffset,

        '&::after': {
          content: "''",
          height: StylesRangeVariables(theme).track.height,
          background: rgba(theme.color.light.base, theme.opacity.less),
          transition: `background ${theme.timing.base} ${theme.transition.base}`,
          position: 'absolute',
          zIndex: 1,
          bottom: rangeThumbOffset,
          left: 0,
          width: '100%',
        },

        '&:hover::after, &:focus-within::after': {
          background: theme.color.primary.base,
        },

        [theme.mq.medium]: {
          gridArea: 'aside',
          top: -2, // 💩 Magic-number offset for better alignment because ranges are insanely inconsistent
        },
      }}
    >
      <div css={{ position: 'relative' }}>
        {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          css={[
            text.heading(theme),
            {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              fontSize: theme.fontSize.xlarge,
              cursor: 'text',
              zIndex: inputActive ? -1 : 1,
            },
          ]}
          onClick={() => {
            setInputActive(true)
            inputRef.current.focus()
          }}
        >
          {getLabel(valueVariable, control.type)}
        </div>
        {/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <Input
          ref={inputRef}
          type="number"
          css={[
            text.heading(theme),
            {
              border: '0 !important',
              fontSize: theme.fontSize.xlarge,
              zIndex: inputActive ? 1 : -1,
              textAlign: 'center',
            },
          ]}
          name={control.name}
          value={valueVariable}
          onChange={(e) => {
            dispatch({
              type: 'update',
              payload: {
                [control.name]: e.target.value,
              },
            })
          }}
          onBlur={(e) => {
            // Fix any values out of bounds
            if (
              !e.target.value ||
              e.target.value < control.min ||
              e.target.value > control.max
            ) {
              dispatch({
                type: 'update',
                payload: {
                  /* eslint-disable no-nested-ternary */
                  [control.name]:
                    !e.target.value || e.target.value < control.min
                      ? control.min
                      : e.target.value > control.max
                      ? control.max
                      : e.target.value, // Take care of max value validation
                  /* eslint-enable no-nested-ternary */
                },
              })
            }

            setInputActive(false)
          }}
        />
      </div>
    </div>
  )
}

CalculatorQuestionInput.propTypes = {
  control: PropTypes.shape({
    name: PropTypes.string.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    type: PropTypes.oneOf(['percentage', 'blank', 'currency', 'monthly'])
      .isRequired,
  }),
}

const CalculatorLegendLabel = ({ align = 'left', children, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={[
        text.heading(theme),

        align === 'right' && {
          textAlign: 'right',
        },

        {
          letterSpacing: theme.letterSpacing.small,
          color: theme.color.light.base,
          opacity: theme.opacity.less,
        },
      ]}
      {...rest}
    >
      {children}
    </div>
  )
}

CalculatorLegendLabel.propTypes = {
  align: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node.isRequired,
}

const CalculatorQuestionRange = ({ label, description, control, legend }) => {
  const theme = useTheme()

  const {
    dispatch,
    state: { [control.name]: valueVariable },
  } = useContext(ROIContext)

  return (
    <Fragment>
      <div
        css={{
          [theme.mq.medium]: {
            gridArea: 'main',
          },
        }}
      >
        <div>
          <span
            css={[
              text.heading(theme),
              {
                letterSpacing: theme.letterSpacing.small,
                lineHeight: theme.lineHeight.small,
              },
            ]}
          >
            {label}
          </span>
          <Tooltip content={description} variation="dark">
            <Icon
              icon="question-circle"
              css={{
                marginLeft: theme.space.small,
                opacity: theme.opacity.base,
              }}
            />
          </Tooltip>
        </div>
        <Range
          contrast
          css={{ borderRadius: 0 }}
          value={valueVariable}
          onChange={(e) => {
            dispatch({
              type: 'update',
              payload: {
                [control.name]: e.target.value,
              },
            })
          }}
          name={control.name}
          step={control.step}
          min={control.min}
          max={control.max}
        />
      </div>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: theme.space.small,
          userSelect: 'none',

          [theme.mq.medium]: {
            gridArea: 'below',
          },
        }}
      >
        <CalculatorLegendLabel>{legend.min}</CalculatorLegendLabel>
        <CalculatorLegendLabel align="right">
          {legend.max}
        </CalculatorLegendLabel>
      </div>
    </Fragment>
  )
}

CalculatorQuestionRange.propTypes = {
  description: PropTypes.any,
  label: PropTypes.any,
  legend: PropTypes.shape({
    min: PropTypes.string.isRequired,
    max: PropTypes.string.isRequired,
  }),
  control: PropTypes.shape({
    name: PropTypes.string.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired,
  }),
}

const CalculatorQuestion = ({ label, description, control, legend }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        columnGap: theme.space.base,
        rowGap: theme.space.xsmall,

        [theme.mq.medium]: {
          gridTemplateAreas: `"aside main"
                              "misc below"`,
          gridTemplateColumns: '250px 1fr',
          alignItems: 'end',
        },
      }}
    >
      <CalculatorQuestionInput control={control} />
      <CalculatorQuestionRange
        label={label}
        description={description}
        control={control}
        legend={legend}
      />
    </div>
  )
}

CalculatorQuestion.propTypes = {
  description: PropTypes.any,
  label: PropTypes.any,
  legend: PropTypes.shape({
    min: PropTypes.string.isRequired,
    max: PropTypes.string.isRequired,
  }),
  control: PropTypes.shape({
    name: PropTypes.string.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired,
    type: PropTypes.oneOf(['percentage', 'blank', 'currency', 'monthly'])
      .isRequired,
  }),
}

const Calculator = () => {
  const theme = useTheme()

  return (
    <div css={{ display: 'grid', gap: theme.space.large }}>
      <CalculatorQuestion
        label="Revenue per year for your applications"
        description="What is the total revenue per year that is dependent on your applications’ uptime?"
        control={{
          min: 50000,
          max: 15000000000,
          step: 50000,
          name: 'R',
          type: 'currency',
        }}
        legend={{
          min: '$50,000',
          max: '$15,000,000,000',
        }}
      />
      <CalculatorQuestion
        label="Number of services/containers"
        description="How many services or containers do you have in your various applications?"
        control={{
          min: 1,
          max: 300000,
          step: 1,
          name: 'N',
          type: 'blank',
        }}
        legend={{
          min: '1',
          max: '300,000+',
        }}
      />
      <CalculatorQuestion
        label="Container refresh rate"
        description="How often are your containers replaced?"
        control={{
          min: 1,
          max: 30,
          step: 1,
          name: 'CL',
          type: 'monthly',
        }}
        legend={{
          min: '1',
          max: '30+',
        }}
      />
      <CalculatorQuestion
        label="Number of developer and operation teams"
        description="How many application teams do you have that develop and operate your systems?"
        control={{
          min: 1,
          max: 2000,
          step: 1,
          name: 'DO',
          type: 'blank',
        }}
        legend={{
          min: '1',
          max: '2,000+',
        }}
      />
      <CalculatorQuestion
        label="Hours of downtime per year"
        description="How many hours of downtime did your application have the last 12 months?"
        control={{
          min: 1.0,
          max: 90.0,
          step: 0.5,
          name: 'D',
          type: 'blank',
        }}
        legend={{
          min: '1',
          max: '90+',
        }}
      />
      <CalculatorQuestion
        label="High severity incidents per year"
        description="How many high severity (SEV 0 & 1) incidents did your applications face in the last 12 months?"
        control={{
          min: 1,
          max: 100,
          step: 1,
          name: 'S',
          type: 'blank',
        }}
        legend={{
          min: '1',
          max: '100+',
        }}
      />
      <CalculatorQuestion
        label="Average hours to resolve incidents, create patches, and perform post-mortems"
        description="How long, on average, did it take to resolve the high severity incidents, and on “day 2”, create patches and perform post-mortems?"
        control={{
          min: 10,
          max: 100,
          step: 1,
          name: 'HR',
          type: 'blank',
        }}
        legend={{
          min: '10',
          max: '100+',
        }}
      />
      <CalculatorQuestion
        label="Average number of employees resolving issues"
        description="On average, how many administrators, architects, SREs, engineers, etc. worked to troubleshoot and resolve each issue?"
        control={{
          min: 1,
          max: 10000,
          step: 1,
          name: 'E',
          type: 'blank',
        }}
        legend={{
          min: '1',
          max: '10,000+',
        }}
      />
      <CalculatorQuestion
        label="Annual salary of employees resolving issues"
        description="What is the average base salary of employees who troubleshoot and resolve high severity incidents?"
        control={{
          min: 60000,
          max: 400000,
          step: 5000,
          name: 'ES',
          type: 'currency',
        }}
        legend={{
          min: '$60,000',
          max: '$400,000+',
        }}
      />
      <CalculatorQuestion
        label="Number of employees impacted by incidents"
        description="How many employees not mentioned in the previous point are impacted by a high severity incident?"
        control={{
          min: 10,
          max: 100000,
          step: 10,
          name: 'U',
          type: 'blank',
        }}
        legend={{
          min: '10',
          max: '100,000+',
        }}
      />
      <CalculatorQuestion
        label="Annual salary of employees impacted"
        description="What is the average base salary of the employees not troubleshooting, but still impacted?"
        control={{
          min: 60000,
          max: 200000,
          step: 10000,
          name: 'US',
          type: 'currency',
        }}
        legend={{
          min: '$60,000',
          max: '$200,000+',
        }}
      />
      <CalculatorQuestion
        label="Percent of employees’ productivity impacted"
        description="What percent of your employees’ productivity is impacted when an application goes down?"
        control={{
          min: 5,
          max: 50,
          step: 5,
          name: 'P',
          type: 'percentage',
        }}
        legend={{
          min: '5%',
          max: '50%+',
        }}
      />
    </div>
  )
}

const CalcBox = ({ children, label }) => {
  const theme = useTheme()

  return (
    <div
      css={[
        text.heading(theme),
        {
          borderRadius: theme.borderRadius.base,
          background:
            'linear-gradient(to bottom, rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0))',
          padding: theme.space.base,
          display: 'grid',
          gap: theme.space.base,
          textAlign: 'center',
          textTransform: 'uppercase',

          [theme.mq.medium]: {
            padding: theme.space.large,
          },
        },
      ]}
    >
      <div
        css={{
          fontSize: theme.fontSize.h1 * 1.5,
          lineHeight: 1,

          sup: {
            fontSize: '50%',
            top: '-1em',
          },
        }}
      >
        {children}
      </div>
      <div css={[tealGreen(theme), { height: 2 }]} />
      <div css={{ fontSize: theme.fontSize.xlarge }}>{label}</div>
    </div>
  )
}

CalcBox.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
}

const InvestmentCalculation = () => {
  const theme = useTheme()

  const { state } = useContext(ROIContext)

  const [isCopied, setCopied] = useState(false)

  const calculations = useMemo(() => roiCalculations(state), [state])

  // Reset icon after 3 seconds
  useUpdateEffect(() => {
    if (isCopied) {
      // Pass-through tracking event and complete state object
      track('ROI link copied', {
        state,
      })

      setTimeout(() => setCopied(false), 3000)
    }
  }, [isCopied])

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space.large,
        marginTop: theme.space.xlarge,
        marginBottom: theme.space.xlarge,

        [theme.mq.medium]: {
          gridTemplateColumns: '1fr 450px',
        },
      }}
    >
      <div
        css={{
          [theme.mq.small__max]: {
            textAlign: 'center',
          },
        }}
      >
        <div
          css={[
            text.heading(theme),
            theme.fontSize.h2__fluid,
            {
              textTransform: 'uppercase',
            },
          ]}
        >
          4 year investment
        </div>
        <div
          css={[
            text.heading(theme),
            theme.fontSize.h3__fluid,
            {
              textTransform: 'uppercase',
              color: theme.color.primary.base,
            },
          ]}
        >
          in Chaos Engineering
        </div>
        <div
          css={{
            marginTop: theme.space.base,
            display: 'grid',
            gridAutoFlow: 'column',
            gridTemplateColumns: 'repeat(2, max-content)',
            gap: theme.space.base,
            alignItems: 'center',

            [theme.mq.small__max]: {
              justifyContent: 'center',
            },
          }}
        >
          <Icon
            icon="share-alt"
            css={{
              width: theme.height.base,
              height: theme.height.base,
              top: 0,
            }}
          />
          <CopyToClipboard
            text={generateUrl(state)}
            onCopy={() => setCopied(true)}
          >
            <Button type="default">
              {isCopied ? 'Link copied!' : 'Share results'}
            </Button>
          </CopyToClipboard>
        </div>
      </div>
      <div
        css={{
          justifyContent: 'center',
          display: 'grid',
          gridAutoColumns: '1fr',
          gridAutoFlow: 'column',
          gap: theme.space.small,
          alignItems: 'start',
        }}
      >
        <CalcBox label="ROI">
          {calculations.roi}
          <sup>%</sup>
        </CalcBox>
        <CalcBox label="Payback period">
          {calculations.paybackPeriod}
          <sup>yr</sup>
        </CalcBox>
      </div>
    </div>
  )
}

const Bottom = () => {
  const theme = useTheme()

  return (
    <div css={{ textAlign: 'center' }}>
      <Quote contrastQuotes size="large">
        By 2023, 40% of organizations will implement chaos engineering practices
        as part of DevOps initiatives, reducing unplanned downtime by 20%
      </Quote>
      <GartnerSVG
        css={{
          filter: 'brightness(0) invert(1)',
          height: theme.height.xsmall,
        }}
      />
      <Alert type="primary" css={{ marginTop: theme.space.large }}>
        This assumes standard terms, no services, and average frequency of
        attacks for usage.{' '}
        <br css={{ [theme.mq.small__max]: { display: 'none' } }} />
        For a customized analysis, please{' '}
        <TextLink href="/contact/">contact us</TextLink>.
      </Alert>
    </div>
  )
}

const PageContent = () => {
  const { dispatch } = useContext(ROIContext)

  useEffect(() => {
    const queryVars = qs.parse(window?.location?.search, {
      ignoreQueryPrefix: true,
    })

    // If we have query args onload, update context to match and re-calculate
    // @NOTE This would be for someone revisiting the page after selecting "Share results"
    if (!isEmpty(queryVars)) {
      dispatch({
        type: 'update',
        payload: queryVars,
      })
    }
  }, [dispatch])

  return (
    <Section className="u-contrast">
      <Container>
        <Calculator />
        <InvestmentCalculation />
        <Bottom />
      </Container>
    </Section>
  )
}

const ROI = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout
      headerProps={{
        sticky: false,
      }}
      footerProps={{
        hideCTA: true,
      }}
      siteWrapperProps={{
        css: [gradient.dark(theme)],
      }}
    >
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
        noIndex
      />
      <Hero compact>
        <HeroContent>
          <HomeHeroGrid>
            <HomeHeroGridImage
              css={{
                [theme.mq.small__max]: {
                  textAlign: 'center',

                  svg: {
                    maxWidth: 400,
                  },
                },

                [theme.mq.medium]: {
                  svg: { height: 350 },
                },
              }}
            >
              <HeroSVG />
            </HomeHeroGridImage>
            <HomeHeroGridContent>
              <div
                css={{
                  marginBottom: theme.space.large,

                  [theme.mq.small__max]: {
                    textAlign: 'center',
                  },
                }}
              >
                <Logo
                  variation="white"
                  css={{
                    maxWidth: 250,

                    [theme.mq.medium]: {
                      maxWidth: 350,
                    },
                  }}
                />
                <div
                  css={[
                    text.heading(theme),
                    theme.fontSize.h3__fluid,
                    {
                      color: theme.color.primary.base,
                      textTransform: 'uppercase',
                    },
                  ]}
                >
                  ROI Calculator
                </div>
              </div>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
            </HomeHeroGridContent>
          </HomeHeroGrid>
        </HeroContent>
      </Hero>
      <ROIProvider>
        <PageContent pageData={pageData} />
      </ROIProvider>
    </BaseLayout>
  )
}

ROI.propTypes = {
  data: PropTypes.object.isRequired,
}

export default ROI

export const pageQuery = graphql`
  query ROIPageData {
    file(name: { eq: "roi" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          socialMediaImage
          hero {
            blurb
          }
        }
      }
    }
  }
`
